import { Dispatch, FC, SetStateAction, useEffect } from 'react'
import {
	Control,
	Controller,
	FormState,
	UseFormTrigger,
	useWatch
} from 'react-hook-form'

import { Button, Typography } from '@/components/ui'
import CustomInput from '@/components/ui/input/CustomInput'
import CustomSelect from '@/components/ui/select/CustomSelect'
import CustomTextarea from '@/components/ui/textarea/CustomTextArea'

import { useAuth } from '@/auth/useAuth'
import { selectToFollowByTaskId } from '@/store/features/formUploader/formUploaderSelectors'
import { useAppSelector } from '@/store/hooks'
import { IOption } from '@/types/selectOption.interface'
import { IShotlist } from '@/types/shotlist.interface'
import { ILanguage } from '@/types/task.interface'
import {
	IPlatform,
	ISelectedPlatform,
	IUploaderForm,
	TPlatformValue
} from '@/types/uploadForm.interface'

import { useFormContentSubmit } from '../hooks/useFormContentSubmit'
import { isSourceRequired } from '../utils'

import FormHeader from './FormHeader'
import { SuccessScreen } from './SuccessScreen'

const RIGHTS_OPTIONS: IOption[] = [
	{ label: 'Exclusive', value: 'exclusive' },
	{ label: 'Non-exclusive', value: 'non-exclusive' },
	{ label: 'Pool', value: 'pool' }
]

const UAE_OPTIONS: IOption[] = [
	{ label: 'True', value: 'true' },
	{ label: 'False', value: 'false' }
]

interface IFormContentProps {
	control: Control<IUploaderForm>
	handleFieldChange: (
		fieldName: keyof IUploaderForm,
		value: IUploaderForm[keyof IUploaderForm]
	) => void
	countryOptions: IOption[]
	taskId: string
	selectedPlatform: TPlatformValue | null
	tagsOptions: IOption[]
	onSubmit: () => Promise<void>
	regionsOptions: IOption[]
	categoriesOptions: IOption[]
	isUploaded?: boolean
	formState: FormState<IUploaderForm>
	trigger: UseFormTrigger<IUploaderForm>
	isSuccess?: boolean
	hasUploadedVideo?: boolean
	deletePlatform?: (value: TPlatformValue) => void
	platformList?: IPlatform[] | null
	languages?: ILanguage[]
	isUploading?: boolean
	isTranscribing?: boolean
	handleTranscribe?: (value: string) => void
	handleTranslate?: (value: string) => void
	handleUploadSrt?: (value: File) => void
	selectedTranscribingLang?: ILanguage | null
	setSelectedTranscribingLang?: Dispatch<SetStateAction<ILanguage | null>>
	isTranslating?: boolean
	selectedPlatformList?: ISelectedPlatform[]
	availableSourceLanguages?: IOption[]
	shotlist?: IShotlist | null
	pendingSrtFileName?: string
	handleCloseErrorMessage?: () => void
	handleDeleteTranslation?: () => void
	onStopTranscription?: () => void
	handleAutoFill: () => void
	sourceAutoFillLang: TPlatformValue | ''
	setSourceAutoFillLang: Dispatch<SetStateAction<TPlatformValue | ''>>
	isProcessing: boolean
}

const FormContent: FC<IFormContentProps> = ({
	control,
	handleFieldChange,
	countryOptions,
	taskId,
	selectedPlatform,
	tagsOptions,
	onSubmit,
	categoriesOptions,
	regionsOptions,
	isUploaded,
	formState,
	trigger,
	isSuccess,
	hasUploadedVideo,
	deletePlatform,
	platformList,
	languages,
	isUploading,
	isTranscribing,
	handleTranscribe,
	handleTranslate,
	handleUploadSrt,
	selectedTranscribingLang,
	setSelectedTranscribingLang,
	isTranslating,
	selectedPlatformList,
	availableSourceLanguages,
	shotlist,
	pendingSrtFileName,
	handleCloseErrorMessage,
	handleDeleteTranslation,
	onStopTranscription,
	handleAutoFill,
	sourceAutoFillLang,
	setSourceAutoFillLang,
	isProcessing
}) => {
	const rightsValue = useWatch({ control, name: 'rights' })

	const { isInt, isMain } = useAuth()

	const toFollow = useAppSelector(selectToFollowByTaskId(taskId))

	useEffect(() => {
		trigger('source')
	}, [rightsValue, trigger])

	const {
		currentPlatform,
		selectedPlatformLabel,
		isShowFields,
		handleFormSubmit
	} = useFormContentSubmit({
		taskId,
		selectedPlatform,
		selectedPlatformList,
		platformList,
		toFollow,
		formState,
		trigger,
		onSubmit
	})

	const uploadBtnDisabled = (!isSuccess && !hasUploadedVideo) || isProcessing

	return isUploaded ? (
		<SuccessScreen taskId={taskId} />
	) : (
		<>
			<form
				className='flex flex-col w-[700px] mx-auto gap-[52px]'
				onSubmit={handleFormSubmit}
			>
				<FormHeader
					selectedPlatformLabel={selectedPlatformLabel}
					selectedPlatform={selectedPlatform}
					isUploading={isUploading}
					isTranscribing={isTranscribing}
					deletePlatform={deletePlatform}
					handleTranscribe={handleTranscribe}
					handleTranslate={handleTranslate}
					handleUploadSrt={handleUploadSrt}
					languages={languages}
					selectedTranscribingLang={selectedTranscribingLang}
					setSelectedTranscribingLang={setSelectedTranscribingLang}
					isTranslating={isTranslating}
					taskId={taskId}
					platformState={currentPlatform}
					selectedPlatformList={selectedPlatformList}
					availableSourceLanguages={availableSourceLanguages}
					currentPlatform={currentPlatform}
					shotlist={shotlist}
					pendingSrtFileName={pendingSrtFileName}
					handleCloseErrorMessage={handleCloseErrorMessage}
					handleDeleteTranslation={handleDeleteTranslation}
					onStopTranscription={onStopTranscription}
					handleAutoFill={handleAutoFill}
					sourceAutoFillLang={sourceAutoFillLang}
					setSourceAutoFillLang={setSourceAutoFillLang}
				/>
				<div className='flex flex-col gap-[10px]'>
					<Typography
						variant='text-semibold'
						className='text-[20px] mb-5 !font-bold'
					>
						Main information
					</Typography>
					<div className='flex flex-col gap-[20px]'>
						<div data-field-name='title'>
							<Controller
								name='title'
								control={control}
								rules={{
									required: 'Title is required',
									validate: (value: string) =>
										value.trim() !== '' || 'Title is required'
								}}
								render={({ field, fieldState }) => (
									<CustomInput
										{...field}
										onBlur={() => {
											field.onBlur()
											handleFieldChange('title', field.value)
										}}
										onClear={() => {
											handleFieldChange('title', '')
										}}
										fieldLabel='Title'
										iconName='clear'
										placeholder='Title'
										error={!!fieldState.error}
									/>
								)}
							/>
						</div>
						<div data-field-name='description'>
							<Controller
								name='description'
								control={control}
								render={({ field, fieldState }) => (
									<CustomTextarea
										{...field}
										onBlur={() => {
											field.onBlur()
											handleFieldChange('description', field.value)
										}}
										onClear={() => {
											handleFieldChange('description', '')
										}}
										fieldLabel='Description'
										iconName='clear'
										placeholder='SCRIPT TO FOLLOW'
										error={!!fieldState.error}
									/>
								)}
							/>
						</div>
						<div data-field-name='location'>
							<Controller
								name='location'
								control={control}
								rules={{
									required: 'Location is required',
									validate: (value: string) =>
										value.trim() !== '' || 'Location is required'
								}}
								render={({ field, fieldState }) => (
									<CustomInput
										{...field}
										onBlur={() => {
											field.onBlur()
											handleFieldChange('location', field.value)
										}}
										onClear={() => {
											handleFieldChange('location', '')
										}}
										fieldLabel='Location'
										iconName='clear'
										placeholder='Select location'
										error={!!fieldState.error}
									/>
								)}
							/>
						</div>
					</div>
				</div>
				{isShowFields && (
					<div className='flex flex-col gap-[10px]'>
						<Typography
							variant='text-semibold'
							className='text-[20px] mb-5 !font-bold'
						>
							Categories and tags
						</Typography>
						<div className='flex flex-col gap-[20px]'>
							<div data-field-name='categories'>
								<Controller
									name='categories'
									control={control}
									rules={{
										required: 'At least one category is required',
										validate: (value: IOption[]) =>
											(value && value.length > 0) ||
											'At least one category is required'
									}}
									render={({ field, fieldState }) => (
										<CustomSelect
											{...field}
											onBlur={() => {
												handleFieldChange('categories', field.value)
											}}
											options={categoriesOptions}
											placeholder='Add categories'
											isDropdownIndicator='none'
											isMulti={true}
											error={!!fieldState.error}
										/>
									)}
								/>
							</div>
							<Controller
								name='tags'
								control={control}
								render={({ field }) => (
									<CustomSelect
										{...field}
										onBlur={() => {
											handleFieldChange('tags', field.value)
										}}
										options={tagsOptions}
										placeholder='Add tags'
										isDropdownIndicator='none'
										isMulti={true}
									/>
								)}
							/>
						</div>
					</div>
				)}
				<div className='flex flex-col gap-[10px]'>
					<Typography
						variant='text-semibold'
						className='text-[20px] mb-5 !font-bold'
					>
						Settings
					</Typography>
					<div className='flex flex-col gap-[20px]'>
						{isShowFields && (
							<>
								<div data-field-name='region'>
									<Controller
										name='region'
										control={control}
										rules={{
											required: 'Region is required'
										}}
										render={({ field, fieldState }) => (
											<CustomSelect
												{...field}
												onBlur={() => {
													handleFieldChange('region', field.value)
												}}
												options={regionsOptions}
												placeholder='Region'
												isMulti={false}
												error={!!fieldState.error}
											/>
										)}
									/>
								</div>
								<div data-field-name='country'>
									<Controller
										name='country'
										control={control}
										rules={{
											required: 'Country is required'
										}}
										render={({ field, fieldState }) => (
											<CustomSelect
												{...field}
												onBlur={() => {
													handleFieldChange('country', field.value)
												}}
												options={countryOptions}
												placeholder='Country'
												isMulti={false}
												error={!!fieldState.error}
											/>
										)}
									/>
								</div>
								<div data-field-name='rights'>
									<Controller
										name='rights'
										control={control}
										rules={{
											required: 'Rights is required',
											validate: (value: string) =>
												value.trim() !== '' || 'Rights is required'
										}}
										render={({ field, fieldState }) => {
											// Convert string value to IOption for CustomSelect
											const selectedOption =
												RIGHTS_OPTIONS.find(opt => opt.value === field.value) ||
												null

											// Convert IOption back to string when changed
											const handleChange = (option: IOption | null) => {
												const stringValue = option ? option.value : ''
												field.onChange(stringValue)
												handleFieldChange('rights', stringValue)
											}

											return (
												<CustomSelect
													value={selectedOption}
													onChange={handleChange}
													onBlur={field.onBlur}
													options={RIGHTS_OPTIONS}
													placeholder='Rights'
													isMulti={false}
													error={!!fieldState.error}
												/>
											)
										}}
									/>
								</div>
								<div data-field-name='UAERegulatedContent'>
									<Controller
										name='UAERegulatedContent'
										control={control}
										rules={{
											required: 'UAE-regulated content is required',
											validate: (value: string) =>
												value.trim() !== '' ||
												'UAE-regulated content is required'
										}}
										render={({ field, fieldState }) => {
											// Convert string value to IOption for CustomSelect
											const selectedOption =
												UAE_OPTIONS.find(opt => opt.value === field.value) ||
												null

											// Convert IOption back to string when changed
											const handleChange = (option: IOption | null) => {
												const stringValue = option ? option.value : ''
												field.onChange(stringValue)
												handleFieldChange('UAERegulatedContent', stringValue)
											}

											return (
												<CustomSelect
													value={selectedOption}
													onChange={handleChange}
													onBlur={field.onBlur}
													options={UAE_OPTIONS}
													placeholder='UAE-regulated content'
													isMulti={false}
													error={!!fieldState.error}
												/>
											)
										}}
									/>
								</div>
							</>
						)}
						<div data-field-name='source'>
							<Controller
								name='source'
								control={control}
								rules={{
									validate: (value: string, formValues: IUploaderForm) => {
										const required = isSourceRequired({
											rights: formValues?.rights ?? rightsValue,
											isInt: Boolean(isInt),
											isMain: Boolean(isMain)
										})

										return (
											!required || value.trim() !== '' || 'Source is required'
										)
									}
								}}
								render={({ field, fieldState }) => (
									<CustomInput
										{...field}
										onBlur={() => {
											field.onBlur()
											handleFieldChange('source', field.value)
										}}
										onClear={() => {
											handleFieldChange('source', '')
										}}
										iconName='clear'
										placeholder='Source'
										error={!!fieldState.error}
									/>
								)}
							/>
						</div>
						<Controller
							name='restrictions'
							control={control}
							render={({ field }) => (
								<CustomInput
									{...field}
									onBlur={() => {
										field.onBlur()
										handleFieldChange('restrictions', field.value)
									}}
									onClear={() => {
										handleFieldChange('restrictions', '')
									}}
									placeholder='Restrictions'
									iconName='clear'
								/>
							)}
						/>
					</div>
				</div>
				<div className='flex flex-col gap-[10px] mb-8'>
					<Button
						className='bg-gray-600 px-9 py-4 rounded-[8px] disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-gray-600'
						classNameText='text-[18px]'
						type='submit'
						disabled={uploadBtnDisabled}
					>
						Upload
					</Button>
				</div>
			</form>
		</>
	)
}

export default FormContent
